/**
 * WaveCreationPage — page-level wrapper for <MotionWaveSelectPanel>.
 *
 * Route: `/ivt/motion/wave-creation?motionId={id}`
 *
 * Reads `motionId` from the URL search params and renders the panel. On
 * successful Confirm or Cancel, navigates back to the Motion detail page
 * (route `/ivt/motions/:motionId`) — Create Wave is only reachable *from*
 * the detail page, so both outcomes deposit the user back there.
 *
 * A breadcrumb (Motions > <motion name> > Create Wave) mirrors the detail
 * page so the origin of this flow is legible and clickable.
 *
 * Owner split (2026-06-30): fuxin owns Create Wave; the Motion list and
 * Motion detail pages are owned by Fan and Wenyuan respectively. This page
 * is intentionally self-contained so the detail page can later mount the
 * panel directly via the component import (`<MotionWaveSelectPanel ... />`)
 * without going through this page.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  BreadcrumbGroup,
  Button,
  Container,
  Header,
} from '@amzn/awsui-components-react';
import MotionWaveSelectPanel from '../../components/MotionWaveSelectPanel/MotionWaveSelectPanel';
import { getIvtClient, GetMotionCommand } from '../../client/IvtHubClient';
import './WaveCreationPage.css';

export default function WaveCreationPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  // motionId is opaque server-generated TEXT (32-char lowercase hex, V14+).
  // Do NOT parseInt — that silently truncates on the first non-digit character.
  const motionId = params.get('motionId');

  const motionDetailHref = motionId ? `/ivt/motions/${motionId}` : '/ivt/motions';

  // Both Confirm and Cancel land back on the Motion detail page — Create Wave
  // is opened from a "Create New Waves" button there, so returning to the same
  // page matches the "close this side-quest" mental model. On Confirm the new
  // claims are already visible in the pool table; on Cancel nothing changed.
  const handleConfirmed = useCallback(() => {
    navigate(motionDetailHref);
  }, [motionDetailHref, navigate]);

  const handleCancel = useCallback(() => {
    navigate(motionDetailHref);
  }, [motionDetailHref, navigate]);

  // Fetch the Motion name for the breadcrumb. Non-blocking: if the call
  // fails, fall back to the motionId so the crumb still renders.
  const [motionName, setMotionName] = useState<string | null>(null);
  useEffect(() => {
    if (!motionId) return;
    let cancelled = false;
    (async () => {
      try {
        const out = await getIvtClient().send(new GetMotionCommand({ motionId }));
        if (!cancelled) setMotionName(out.motion?.name ?? null);
      } catch {
        // supplementary — breadcrumb falls back to motionId
      }
    })();
    return () => { cancelled = true; };
  }, [motionId]);

  // ---- Bad / missing motionId ---------------------------------------------
  // Server ids match ^[a-z0-9]+$ (Smithy MotionId pattern); reject anything
  // else at the page boundary so downstream code doesn't have to null-guard.
  if (!motionId || !/^[a-z0-9]+$/.test(motionId)) {
    return (
      <div className="ivt-wave-creation-page">
        <Container header={<Header variant="h2">Missing motion</Header>}>
          <Alert type="error" header="No motion specified">
            This page needs a <code>?motionId=</code> query parameter. Open
            Create Wave from a specific Motion.
          </Alert>
          <Box margin={{ top: 's' }}>
            <Button onClick={() => navigate('/ivt/motions')}>Back to Motions</Button>
          </Box>
        </Container>
      </div>
    );
  }

  // ---- Main: breadcrumb + panel -------------------------------------------
  // No page-level header — the <MotionWaveSelectPanel> Container header
  // already says "Create Wave"; stacking another title would duplicate it.
  return (
    <div className="ivt-wave-creation-page">
      <div className="ivt-wave-creation-page__breadcrumb">
        <BreadcrumbGroup
          items={[
            { text: 'Motions', href: '/ivt/motions' },
            { text: motionName ?? motionId, href: motionDetailHref },
            { text: 'Create Wave', href: '' },
          ]}
          onFollow={(e) => {
            e.preventDefault();
            if (e.detail.href) navigate(e.detail.href);
          }}
        />
      </div>
      <MotionWaveSelectPanel
        motionId={motionId}
        onConfirmed={handleConfirmed}
        onCancel={handleCancel}
      />
    </div>
  );
}
